// terminal/src/core/terminal-manager.ts

/**
 * TerminalManager maintains a Map of sessionId → SessionController.
 * Handles message routing, show/hide on tab switch, and lifecycle.
 * 
 * No E2EE awareness — by the time messages reach here, they're plaintext with session_id.
 */

import { SessionController } from './session-controller';
import type { ITheme } from '@xterm/xterm';

export interface TerminalManagerConfig {
  parentContainer: HTMLElement;
  /** Default theme for new sessions */
  defaultTheme?: ITheme;
  /** Called when a session's terminal produces input */
  onInput: (sessionId: string, data: string) => void;
  /** Called when a session's terminal resizes */
  onResize: (sessionId: string, rows: number, cols: number) => void;
  /** Called when output arrives for a non-focused session (activity indicator) */
  onActivity?: (sessionId: string) => void;
}

export class TerminalManager {
  private sessions: Map<string, SessionController> = new Map();
  /** Supports multiple visible sessions for future split panes */
  private visibleSessionIds: Set<string> = new Set();
  /** Which session currently has keyboard focus */
  private focusedSessionId: string | null = null;
  private config: TerminalManagerConfig;

  constructor(config: TerminalManagerConfig) {
    this.config = config;
  }

  // --- Lifecycle ---

  createSession(sessionId: string, theme?: ITheme): SessionController {
    // If session already exists, return it
    const existing = this.sessions.get(sessionId);
    if (existing) return existing;

    const controller = new SessionController({
      sessionId,
      parentContainer: this.config.parentContainer,
      onInput: this.config.onInput,
      onResize: this.config.onResize,
      ...(theme ? { theme } : {}),
      ...(this.config.defaultTheme ? { defaultTheme: this.config.defaultTheme } : {}),
    });

    this.sessions.set(sessionId, controller);
    return controller;
  }

  destroySession(sessionId: string): void {
    const controller = this.sessions.get(sessionId);
    if (!controller) return;

    this.visibleSessionIds.delete(sessionId);
    if (this.focusedSessionId === sessionId) {
      this.focusedSessionId = null;
    }

    controller.dispose();
    this.sessions.delete(sessionId);
  }

  dispose(): void {
    for (const controller of this.sessions.values()) {
      controller.dispose();
    }
    this.sessions.clear();
    this.visibleSessionIds.clear();
    this.focusedSessionId = null;
  }

  // --- Routing ---

  routeOutput(sessionId: string, data: string | Uint8Array): void {
    const controller = this.sessions.get(sessionId);
    if (!controller) {
      // Output for unknown session — could be a race condition during creation
      console.warn(`[TerminalManager] Output for unknown session: ${sessionId}`);
      return;
    }

    controller.write(data);

    // If this session is not focused, notify for activity indicator
    if (sessionId !== this.focusedSessionId && this.config.onActivity) {
      this.config.onActivity(sessionId);
    }
  }

  routeScrollback(sessionId: string, lines: string[], totalLines: number, savedAt?: number): void {
    const controller = this.sessions.get(sessionId);
    if (!controller) return;
    controller.writeScrollback(lines, totalLines, savedAt);
  }

  // --- Switching ---

  switchTo(sessionId: string): void {
    const controller = this.sessions.get(sessionId);
    if (!controller) return;

    // Hide all currently visible sessions
    for (const visibleId of this.visibleSessionIds) {
      if (visibleId !== sessionId) {
        this.sessions.get(visibleId)?.hide();
      }
    }

    // Show the target session
    this.visibleSessionIds.clear();
    this.visibleSessionIds.add(sessionId);
    this.focusedSessionId = sessionId;
    controller.show();
  }

  getActive(): SessionController | null {
    if (!this.focusedSessionId) return null;
    return this.sessions.get(this.focusedSessionId) ?? null;
  }

  getActiveSessionId(): string | null {
    return this.focusedSessionId;
  }

  // --- Resize ---

  fitActive(): { rows: number; cols: number } {
    const active = this.getActive();
    if (!active) return { rows: 24, cols: 80 };
    return active.fit();
  }

  // --- Access ---

  getSession(sessionId: string): SessionController | null {
    return this.sessions.get(sessionId) ?? null;
  }

  /** Write to the focused terminal (e.g., file explorer path insert) */
  writeToActive(data: string): void {
    this.getActive()?.write(data);
  }

  /** Focus the active terminal (e.g., after sidebar toggle) */
  focusActive(): void {
    this.getActive()?.focus();
  }

  /** Get count of managed sessions */
  get size(): number {
    return this.sessions.size;
  }

  // --- Session transfer ---

  transferSession(fromSessionId: string, toSessionId: string): void {
    const controller = this.sessions.get(fromSessionId);
    if (!controller) return;

    this.sessions.delete(fromSessionId);
    controller.updateSessionId(toSessionId);
    this.sessions.set(toSessionId, controller);

    // Update visibility/focus tracking
    if (this.visibleSessionIds.has(fromSessionId)) {
      this.visibleSessionIds.delete(fromSessionId);
      this.visibleSessionIds.add(toSessionId);
    }
    if (this.focusedSessionId === fromSessionId) {
      this.focusedSessionId = toSessionId;
    }
  }
}
